import React from 'react';

import { connect } from 'react-redux';

import { Stack, Box, Table } from '@nerdwallet/currency';
import { Num as num } from '@nerdwallet/nw-client-lib';

import SegmentImpression, {
  useSegmentImpressionContext,
} from '../../../lib/analytics/segment-impression';
import { formatResultForSegment } from '../../../lib/analytics/format-result-for-segment';
import { Browser } from '../../../types';
import { Outputs, CostSchedule } from '../types';

import Cta from './cta';
import PanelHeader from './panel-header';
import ResultsChart from './results-chart/results-chart';
import {
  BUYING_MORE_EXPENSIVE_TEXT,
  RENTING_MORE_EXPENSIVE_TEXT,
  getSummary,
} from './calculations';

export type ResultsPanelProps = {
  dispatch: (args: any) => void;
  trackingFormName: string;
  browser: Browser;
  outputs: Outputs;
  plannedStay: number;
};

type TableProps = {
  rentingScenario: CostSchedule;
  buyingScenario: CostSchedule;
  browser: Browser;
};

const getFormattedOutput = (property, data, browser) => {
  if (browser.isMobile) {
    return num.formatPercent(data[property] ?? 0, '$0a');
  }

  return num.formatCurrency(data[property] ?? 0, false);
};

const getTableProps = ({
  rentingScenario,
  buyingScenario,
  browser,
}: TableProps) => ({
  columns: [
    {
      id: 'column1',
      heading: 'Your cost breakdown',
    },
    {
      id: 'column2',
      heading: 'RENT',
    },
    {
      id: 'column3',
      heading: 'BUY',
    },
  ],
  data: [
    {
      key: 'monthly',
      column1: 'Monthly cost',
      column2: getFormattedOutput('monthlyCostNPV', rentingScenario, browser),
      column3: getFormattedOutput('monthlyCostNPV', buyingScenario, browser),
    },
    {
      key: 'total',
      column1: 'Total cost',
      column2: getFormattedOutput('totalCostNPV', rentingScenario, browser),
      column3: getFormattedOutput('totalCostNPV', buyingScenario, browser),
    },
  ],
});

const isBuyingNeverCheaper = (npvComparisonText) => {
  switch (npvComparisonText) {
    case BUYING_MORE_EXPENSIVE_TEXT:
      return true;
    case RENTING_MORE_EXPENSIVE_TEXT:
      return false;
    default:
      return undefined;
  }
};

const ResultsPanel = ({
  outputs,
  plannedStay,
  browser,
  trackingFormName,
}: ResultsPanelProps): JSX.Element => {
  const { rentingSchedule, buyingSchedule, breakevenYear } = outputs;
  const rentingScenario = rentingSchedule[plannedStay - 1];
  const buyingScenario = buyingSchedule[plannedStay - 1];

  const {
    npvComparisonText,
    formattedMonthlyDiff,
    formattedBenefitNPV,
    savingText,
  } = getSummary(outputs, plannedStay);

  const { eventId: formViewedId } = useSegmentImpressionContext() || {};
  const segmentResult = [
    {
      label: 'is buying will never be cheaper than renting',
      displayValue: isBuyingNeverCheaper(npvComparisonText),
    },
    {
      label: 'If you stay in your home for years',
      displayValue: breakevenYear === null ? undefined : plannedStay,
    },
    {
      label: 'You will save per month',
      displayValue: breakevenYear === null ? undefined : formattedMonthlyDiff,
    },
    {
      label: 'You will save in total',
      displayValue: breakevenYear === null ? undefined : formattedBenefitNPV,
    },
    {
      label: 'break even year',
      displayValue: breakevenYear ?? undefined,
    },
    {
      label: 'monthly cost - rent',
      displayValue: getFormattedOutput(
        'monthlyCostNPV',
        rentingScenario,
        browser
      ),
    },
    {
      label: 'monthly cost - buy',
      displayValue: getFormattedOutput(
        'monthlyCostNPV',
        buyingScenario,
        browser
      ),
    },
    {
      label: 'total cost - rent',
      displayValue: getFormattedOutput(
        'totalCostNPV',
        rentingScenario,
        browser
      ),
    },
    {
      label: 'total cost - buy',
      displayValue: getFormattedOutput('totalCostNPV', buyingScenario, browser),
    },
  ];

  return (
    <Stack>
      <Box padding="3">
        <SegmentImpression
          eventType="feature_viewed"
          attributes={{
            entity_name: 'form_result_text',
            section_name: 'form',
            item_position: null,
            item_copy: 'rent_vs_buy_calculator',
            content_id: null,
            additional_attributes: formatResultForSegment(segmentResult),
            parent_event_id: formViewedId,
          }}
        >
          <PanelHeader title={npvComparisonText} subtitle={savingText} />
          <SegmentImpression
            eventType="feature_viewed"
            attributes={{
              entity_name: 'form_result_graph',
              section_name: 'form',
              item_position: null,
              item_copy: 'rent_vs_buy_calculator',
              content_id: null,
              parent_event_id: formViewedId,
            }}
          >
            <ResultsChart trackingFormName={trackingFormName} />
          </SegmentImpression>
          <Table
            {...getTableProps({ rentingScenario, buyingScenario, browser })}
          />
        </SegmentImpression>
      </Box>
      <Cta />
    </Stack>
  );
};

const mapStateToProps = (state) => ({
  outputs: state?.rentvsbuyCalculator?.outputs,
  plannedStay: state?.rentvsbuyCalculator?.plannedStay,
  browser: state?.browser,
});

export default connect(mapStateToProps)(ResultsPanel);
